import * as docx from 'docx';

import { IFieldEditor } from '@/shared/types/fieldEditor';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { AIFieldType } from '@trace-8/trace8-schema';
import { DefaultTheme } from 'styled-components/dist/types';
import { formatAiFieldValueForTemplate, formatFieldValueForTemplate } from '../../../utils';
import { convertMarkdownToDocx, getAiSuggestedSectionDOCX } from '../../utils';

export const getOneColumnSection = async (
  fields: IFieldEditor[],
  includeAiSuggested: boolean,
  theme: DefaultTheme,
  headerFontColor: string,
  accentColor: string
) => {
  const docxData: (docx.Paragraph | docx.Table)[] = [];

  const createParagraph = ({
    text,
    bold,
    color,
    fontSize,
    spacing
  }: {
    text: string;
    bold: boolean;
    color: string;
    fontSize: number;
    spacing: {
      before: number;
      after: number;
    };
  }) => {
    const formattedText = text?.split('\n').flatMap((line, index, array) => {
      return index < array.length - 1
        ? [
            new docx.TextRun({
              text: line,
              bold,
              color,
              size: `${fontSize}pt`
            }),
            new docx.TextRun({ break: 1 })
          ]
        : new docx.TextRun({
            text: line,
            bold,
            color,
            size: `${fontSize}pt`
          });
    });

    return new docx.Paragraph({
      children: formattedText,
      spacing
    });
  };

  const getFieldData = async (field: IFieldEditor) => {
    const fieldData = [];
    const { attachments, value, commentary } = field;
    const hasIncludedAttachments = attachments?.length;
    const isMatchedWithAI = field.value === null && field.commentary === null && field.AI?.answers?.length && includeAiSuggested;

    if (value || commentary || hasIncludedAttachments) {
      if (fields.length > 1) {
        const titleParagraph = createParagraph({
          text: field.name,
          bold: true,
          color: theme.font.base,
          fontSize: 12,
          spacing: { before: 300, after: 100 }
        });
        if (titleParagraph) {
          fieldData.push(titleParagraph);
        }
      }

      if (field.documentFieldType === getEnumKeyByValue(AIFieldType, AIFieldType.LONG_FORM_CONTENT)) {
        const markdownContent = value || '';
        const markdownParagraphs = await convertMarkdownToDocx({ markdown: markdownContent, attachments, theme, headerFontColor, accentColor });
        fieldData.push(...markdownParagraphs.filter(Boolean));
      } else {
        const paragraph = createParagraph({
          text: formatFieldValueForTemplate(field.documentFieldType, value, commentary),
          bold: false,
          color: theme.font.base,
          fontSize: 10,
          spacing: { before: 0, after: 0 }
        });
        if (paragraph) {
          fieldData.push(paragraph);
        }
      }
    }

    if (isMatchedWithAI) {
      if (fields.length > 1) {
        const titleParagraph = createParagraph({
          text: `* ${field.name}`,
          bold: true,
          color: theme.font.base,
          fontSize: 12,
          spacing: { before: 300, after: 100 }
        });
        if (titleParagraph) {
          fieldData.push(titleParagraph);
        }
      }
      if (field.documentFieldType === getEnumKeyByValue(AIFieldType, AIFieldType.LONG_FORM_CONTENT)) {
        const markdownContent = field.AI.answers[0] || '';
        const markdownParagraphs = await convertMarkdownToDocx({ markdown: markdownContent, attachments, theme, headerFontColor, accentColor });
        fieldData.push(...markdownParagraphs.filter(Boolean));
      } else {
        const paragraph = createParagraph({
          text: formatAiFieldValueForTemplate(field.documentFieldType, field.AI.answers[0]),
          bold: false,
          color: theme.font.base,
          fontSize: 10,
          spacing: { before: 0, after: 0 }
        });
        if (paragraph) {
          fieldData.push(paragraph);
        }
        const aiSuggestedSection = await getAiSuggestedSectionDOCX(fields, theme);
        fieldData.push(...aiSuggestedSection.filter(Boolean));
      }
    }

    return fieldData.filter(Boolean);
  };

  for await (const field of fields) {
    const fieldData = await getFieldData(field);
    docxData.push(...fieldData.filter(Boolean));
  }

  return docxData.filter(Boolean);
};
